import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Minus, Plus, ArrowLeft, ShoppingBag, Heart } from "lucide-react";
import api from "../lib/api";
import { imgSrc, imgSrcSet } from "../lib/imgSrc";
import { registerQrHitFromUrl } from "../lib/qrAttribution";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { PublicShare } from "../components/PublicShare";
import { SITE_URL, publicImage } from "../lib/publicSite";

const ProductDetail = () => {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [qty, setQty] = useState(1);
    const [loading, setLoading] = useState(true);
    // Free-shipping config from CMS — hide the "Free shipping over $X"
    // reassurance line when admin has disabled the feature.
    const [freeEnabled, setFreeEnabled] = useState(null);
    const [freeThreshold, setFreeThreshold] = useState(75);
    const { addItem } = useCart();
    const navigate = useNavigate();

    useEffect(() => {
        setLoading(true); setProduct(null);
        api.get(`/products/${id}`)
            .then((r) => setProduct(r.data))
            .catch(() => setProduct(null))
            .finally(() => setLoading(false));
        // QR analytics — no-op when the URL doesn't carry ?src=qr.
        registerQrHitFromUrl("product", id);
    }, [id]);
    useEffect(() => {
        api.get("/content").then(({ data }) => {
            const enabled = String(data?.["shipping.free_enabled"] ?? "true").toLowerCase() !== "false";
            const thr = parseFloat(data?.["shipping.free_over"] ?? 75);
            setFreeEnabled(enabled);
            setFreeThreshold(Number.isFinite(thr) && thr >= 0 ? thr : 75);
        }).catch(() => setFreeEnabled(true));
    }, []);

    if (loading) {
        return <div className="max-w-7xl mx-auto px-6 py-24 text-[#4A5568]" data-testid="product-loading">Loading…</div>;
    }

    if (!product) {
        return (
            <div className="max-w-7xl mx-auto px-6 py-24 text-center" data-testid="product-not-found">
                <SEO title="Product not found" path={`/product/${encodeURIComponent(id)}`} noIndex />
                <p className="text-[#4A5568] mb-4">Product not found.</p>
                <Link to="/shop" className="text-[#E53935] underline">Back to shop</Link>
            </div>
        );
    }

    const isSponsor = product.category === "donation";
    const stock = typeof product.stock === "number" ? product.stock : null;
    const outOfStock = !isSponsor && stock !== null && stock <= 0;
    const lowStock = !isSponsor && stock !== null && stock > 0 && stock < 5;
    // Cap qty to available stock for physical merch
    const maxQty = isSponsor || stock === null ? 999 : Math.max(1, stock);
    // If qty ever exceeds the current stock (e.g. after admin decrement), clamp.
    if (!isSponsor && stock !== null && stock > 0 && qty > stock) setQty(stock);

    const handleAdd = () => {
        if (outOfStock) return;
        addItem(product, qty);
        toast.success(`${product.name} added to cart`);
    };

    const handleBuyNow = () => {
        if (outOfStock) return;
        addItem(product, qty);
        navigate("/cart");
    };

    // Compose gallery: primary + extras (dedupe against primary)
    const gallery = [product.image_url, ...(product.images || []).filter((u) => u && u !== product.image_url)];

    return <ProductDetailBody
        product={product}
        gallery={gallery}
        qty={qty}
        setQty={setQty}
        handleAdd={handleAdd}
        handleBuyNow={handleBuyNow}
        outOfStock={outOfStock}
        lowStock={lowStock}
        stock={stock}
        isSponsor={isSponsor}
        maxQty={maxQty}
        freeEnabled={freeEnabled}
        freeThreshold={freeThreshold}
    />;
};

const ProductDetailBody = ({ product, gallery, qty, setQty, handleAdd, handleBuyNow, outOfStock, lowStock, stock, isSponsor, maxQty, freeEnabled, freeThreshold }) => {
    const [activeImg, setActiveImg] = useState(gallery[0] || "");
    useEffect(() => { setActiveImg(gallery[0] || ""); }, [product.id, gallery]);

    return (
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12 lg:py-16" data-testid="product-detail-page">
            <SEO title={product.name} description={(product.description || "Shop to support Reddog Community Foundation in Frenchtown, Montana.").slice(0, 170)} path={`/product/${encodeURIComponent(product.id)}`} image={product.image_url} noIndex={product.active === false} jsonLd={product.active === false || isSponsor ? null : {
                "@context": "https://schema.org", "@type": "Product", name: product.name, description: product.description,
                image: publicImage(product.image_url), sku: product.id,
                offers: { "@type": "Offer", url: `${SITE_URL}/product/${encodeURIComponent(product.id)}`, priceCurrency: "USD", price: Number(product.price).toFixed(2), availability: outOfStock ? "https://schema.org/OutOfStock" : "https://schema.org/InStock" },
            }} />
            <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
            <Link
                to="/shop"
                data-testid="product-back-link"
                className="inline-flex items-center gap-2 text-sm text-[#4A5568] hover:text-[#E53935] mb-8 transition-colors"
            >
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                Back to shop
            </Link>
            {product.active !== false && <PublicShare route={`products/${encodeURIComponent(product.id)}`} title={product.name} testId="product-public" />}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
                <div>
                    <div className="aspect-square rounded-3xl overflow-hidden bg-[#F4F1EA]">
                        <img
                            src={imgSrc(activeImg, 1024, 80)}
                            srcSet={imgSrcSet(activeImg, 1024, 80)}
                            alt={product.name}
                            className="w-full h-full object-cover transition-opacity"
                            decoding="async"
                            data-testid="product-detail-image"
                        />
                    </div>
                    {gallery.length > 1 && (
                        <div className="mt-4 flex gap-2.5 overflow-x-auto pb-1" data-testid="product-detail-gallery">
                            {gallery.map((url, i) => (
                                <button
                                    key={`${url}-${i}`}
                                    type="button"
                                    onClick={() => setActiveImg(url)}
                                    data-testid={`product-detail-thumb-${i}`}
                                    aria-label={`View image ${i + 1}`}
                                    className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden bg-[#F4F1EA] border-2 transition-all ${activeImg === url ? "border-[#E53935]" : "border-transparent hover:border-black/20"}`}
                                >
                                    <img
                                        src={imgSrc(url, 160, 70)}
                                        srcSet={imgSrcSet(url, 160, 70)}
                                        alt=""
                                        loading="lazy"
                                        decoding="async"
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div>
                    <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3" data-testid="product-detail-category">
                        {product.category}
                    </p>
                    {product.active === false && (
                        <div className="mb-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium" data-testid="product-detail-scan-only-chip">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                            Off-storefront · scan-only item
                        </div>
                    )}
                    <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium leading-tight mb-6" data-testid="product-detail-name">
                        {product.name}
                    </h1>
                    <p className="font-heading text-3xl text-[#1C252E] font-medium mb-8" data-testid="product-detail-price">
                        ${product.price.toFixed(2)}
                    </p>

                    {/* Stock notices — sold out (red) or low stock (amber). Sponsorships never show these. */}
                    {outOfStock && (
                        <div className="mb-6 rounded-2xl bg-red-50 border border-red-200 px-5 py-4" data-testid="product-detail-out-of-stock">
                            <p className="text-sm font-bold text-red-800 mb-0.5">Currently sold out</p>
                            <p className="text-xs text-red-700">This item is unavailable while we restock. Check back soon or reach out to reddogfoundation@gmail.com to join a wait list.</p>
                        </div>
                    )}
                    {!outOfStock && lowStock && (
                        <div className="mb-6 rounded-2xl bg-amber-50 border border-amber-200 px-5 py-3" data-testid="product-detail-low-stock">
                            <p className="text-sm font-semibold text-amber-800">Only {stock} left in stock — order soon.</p>
                        </div>
                    )}

                    <p className="text-lg text-[#4A5568] leading-relaxed mb-10" data-testid="product-detail-description">
                        {product.description}
                    </p>

                    <div className="flex items-center gap-6 mb-8">
                        <p className="text-sm font-medium text-[#1C252E]">Quantity</p>
                        <div className="flex items-center bg-white border border-black/10 rounded-full" data-testid="product-qty-control">
                            <button
                                onClick={() => setQty(Math.max(1, qty - 1))}
                                data-testid="product-qty-minus"
                                disabled={outOfStock}
                                className="p-3 hover:text-[#E53935] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="decrease"
                            >
                                <Minus className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                            <span className="w-10 text-center font-medium" data-testid="product-qty-value">{qty}</span>
                            <button
                                onClick={() => setQty(Math.min(maxQty, qty + 1))}
                                data-testid="product-qty-plus"
                                disabled={outOfStock || qty >= maxQty}
                                className="p-3 hover:text-[#E53935] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="increase"
                            >
                                <Plus className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                        </div>
                        {!isSponsor && stock !== null && !outOfStock && (
                            <p className="text-xs text-[#4A5568]" data-testid="product-stock-hint">{stock} in stock</p>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <button
                            onClick={handleAdd}
                            disabled={outOfStock}
                            data-testid="product-add-to-cart-btn"
                            className="inline-flex items-center justify-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-8 py-4 font-medium transition-all hover:-translate-y-0.5 disabled:bg-slate-300 disabled:hover:bg-slate-300 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                        >
                            {outOfStock ? (
                                <>Sold out</>
                            ) : product.category === "donation" ? (
                                <>
                                    <Heart className="w-4 h-4" strokeWidth={2} fill="white" />
                                    Sponsor this
                                </>
                            ) : (
                                <>
                                    <ShoppingBag className="w-4 h-4" strokeWidth={2} />
                                    Add to cart
                                </>
                            )}
                        </button>
                        <button
                            onClick={handleBuyNow}
                            disabled={outOfStock}
                            data-testid="product-buy-now-btn"
                            className="inline-flex items-center justify-center gap-2 bg-transparent border border-[#1C252E] text-[#1C252E] hover:bg-[#1C252E] hover:text-white rounded-full px-8 py-4 font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#1C252E]"
                        >
                            {outOfStock ? "Unavailable" : product.category === "donation" ? "Sponsor now" : "Buy now"}
                        </button>
                    </div>

                    <div className="mt-12 pt-8 border-t border-black/10 space-y-3 text-sm text-[#4A5568]">
                        {freeEnabled === true && (
                            <p data-testid="product-free-shipping-line">• Free shipping on orders over ${freeThreshold.toFixed(0)}</p>
                        )}
                        <p>• Every purchase funds Reddog Foundation programs</p>
                        <p>• 30-day return policy on merchandise</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetail;
